import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useSchoolScope } from "@/lib/use-school-scope";
import { usePinFilters } from "@/lib/pins-context";

// Player create form -- family code provided by admin from school system

export default function PlayerCreatePage() {
  const navigate = useNavigate();
  const { schoolId: urlSchoolId } = useParams<{ schoolId: string }>();
  const { schools, selectedSchoolId: scopedSchoolId, setSelectedSchoolId, loading: schoolsLoading, canPickSchool } = useSchoolScope();
  const { activeSchoolIds } = usePinFilters();

  // If URL has a schoolId, use it as initial selection
  useEffect(() => {
    if (urlSchoolId && canPickSchool) {
      setSelectedSchoolId(urlSchoolId);
    }
  }, [urlSchoolId, canPickSchool]);

  // Effective school ID: URL param for single-school users, dropdown selection for multi-school
  const effectiveSchoolId = canPickSchool ? scopedSchoolId : (urlSchoolId || scopedSchoolId);

  // Filter schools by active pins
  const displaySchools = activeSchoolIds.length > 0
    ? schools.filter((s) => activeSchoolIds.includes(s.id))
    : schools;

  const [firstName, setFirstName] = useState("");
  const [surname, setSurname] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [familyCode, setFamilyCode] = useState("");
  const [nickname, setNickname] = useState("");
  const [playerCode, setPlayerCode] = useState("");
  const [grade, setGrade] = useState("");
  const [active, setActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!effectiveSchoolId) return;

    if (!firstName.trim() && !surname.trim()) {
      setError("At least one of first name or surname is required");
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      const gradeNum = grade ? parseInt(grade, 10) : undefined;
      await api.createPlayer(effectiveSchoolId, {
        ...(firstName.trim() ? { first_name: firstName.trim() } : {}),
        ...(surname.trim() ? { surname: surname.trim() } : {}),
        date_of_birth: dateOfBirth,
        ...(familyCode ? { family_code: familyCode } : {}),
        ...(nickname.trim() ? { nickname: nickname.trim() } : {}),
        ...(playerCode.trim() ? { player_code: playerCode.trim() } : {}),
        ...(gradeNum ? { grade: gradeNum } : {}),
        active,
      });
      // 079 -- notify any open age-group pool to re-fetch without a navigation
      window.dispatchEvent(new CustomEvent("player:created"));
      navigate("/players");
    } catch (err) {
      setError(
        err instanceof api.ApiError ? err.message : "Failed to create player",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-page="player-create" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Add Player</h1>
        <p className="text-muted-foreground">
          Create a new player with their family code from the school admin system.
        </p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">Player Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-region="form" data-form="player-create">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            {canPickSchool && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="school">School</Label>
                <select
                  id="school"
                  data-field="school"
                  value={effectiveSchoolId}
                  onChange={(e) => setSelectedSchoolId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  required
                >
                  <option value="">-- Select school --</option>
                  {displaySchools.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Label htmlFor="first-name">First Name</Label>
              <Input
                id="first-name"
                data-field="first-name"
                placeholder="e.g. John"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="surname">Surname</Label>
              <Input
                id="surname"
                data-field="surname"
                placeholder="e.g. Smith"
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                At least one of first name or surname is required.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="dob">Date of Birth</Label>
              <Input
                id="dob"
                data-field="date-of-birth"
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="nickname">Nickname</Label>
              <Input
                id="nickname"
                data-field="nickname"
                placeholder="e.g. Johnny"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                maxLength={100}
              />
              <p className="text-xs text-muted-foreground">
                Optional. What the player is commonly called.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="grade">Grade</Label>
              <select
                id="grade"
                data-field="grade"
                title="Grade"
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">Not set</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((g) => (
                  <option key={g} value={g}>
                    Grade {g}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Optional. Administrative metadata -- not used for age group eligibility.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="player-code">Player Code</Label>
              <Input
                id="player-code"
                data-field="player-code"
                placeholder="e.g. SAMS-12345"
                value={playerCode}
                onChange={(e) => setPlayerCode(e.target.value)}
                maxLength={100}
              />
              <p className="text-xs text-muted-foreground">
                Optional. External school system identifier (e.g. SAMS ID).
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="family-code">Family Code</Label>
              <Input
                id="family-code"
                data-field="family-code"
                placeholder="e.g. ABC123"
                value={familyCode}
                onChange={(e) => setFamilyCode(e.target.value)}
                maxLength={50}
              />
              <p className="text-xs text-muted-foreground">
                Optional. Enter the family code from the school admin system. Siblings share the same code.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <input
                id="active"
                data-field="active"
                type="checkbox"
                title="Active player"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              <Label htmlFor="active">Active player</Label>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting}>
                {submitting ? "Creating..." : "Create Player"}
              </Button>
              <Link
                to="/players"
                className={buttonVariants({ variant: "outline" })}
                data-action="cancel"
              >
                Cancel
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
